import { createSlice } from '@reduxjs/toolkit';

/**
 * Creates a new message object.
 *
 * @param {Object} params - The parameters for the new message.
 * @param {string} [params.content=''] - The content of the message.
 * @param {boolean} [params.isUploading=false] - Indicates if the message is currently being uploaded.
 * @param {boolean} [params.containsFile=false] - Indicates if the message contains a file.
 * @param {string} [params.uploadStatus='pending'] - The upload status of any attached file.
 * @param {number} [params.uploadProgress=0] - The upload progress percentage of any attached file.
 * @param {string} [params.fileUrl=''] - The URL of the uploaded file.
 * @returns {Object} The new message object.
 */
function newMessage(params = {}) {
	return {
		content: params.content ?? '',
		isUploading: params.isUploading ?? false,
		containsFile: params.containsFile ?? false,
		uploadStatus: params.uploadStatus ?? 'pending',
		uploadProgress: params.uploadProgress ?? 0,
		fileUrl: params.fileUrl ?? '',
	};
}

export const messages = createSlice({
	name: 'messages',
	initialState: {
		chat: {
			id: null,
			messages: [],
		},
		newMessage: {
			content: '',
			isUploading: false,
			containsFile: false,
		},
		update: false,
		sendToAPI: false,
		readyToUploadFile: false,
		fileUploading: false,
		messageUploads: {},
	},
	reducers: {
		setUpdate: (state, action) => {
			state.update = action.payload.update;
		},
		setNewMessage: (state, action) => {
			state.newMessage = newMessage(action.payload.newMessage);
		},
		setChat: (state, action) => {
			state.chat = action.payload.chat;
		},
		setSendToAPI: (state, action) => {
			state.sendToAPI = action.payload.sendToAPI;
		},
		setReadyToUploadFile: (state, action) => {
			state.readyToUploadFile = action.payload.readyToUploadFile;
		},
		setFileUploading: (state, action) => {
			state.fileUploading = action.payload.fileUploading;
		},
		updateMessageUploadStatus: (state, action) => {
			const { messageId, uploadStatus, uploadProgress, fileUrl, error } =
				action.payload;

			// Create or update the message upload status
			state.messageUploads[messageId] = {
				...state.messageUploads[messageId],
				uploadStatus,
				uploadProgress: uploadProgress || 0,
				fileUrl: fileUrl || state.messageUploads[messageId]?.fileUrl || '',
				error: error || null,
			};

			// If the message is in the chat, update its properties too
			if (state.chat.messages.find((m) => m.id === messageId)) {
				state.chat.messages = state.chat.messages.map((m) =>
					m.id === messageId
						? {
								...m,
								uploadStatus,
								uploadProgress: uploadProgress || 0,
								fileUrl: fileUrl || m.fileUrl || '',
							}
						: m
				);
			}
		},
		resetAfterSend: (state) => {
			state.newMessage = newMessage();
			state.sendToAPI = false;
			state.update = false;
		},
		resetMessagesState: (state) => {
			state.chat = { messages: [] };
			state.newMessage = newMessage();
			state.update = false;
			state.sendToAPI = false;
			state.readyToUploadFile = false;
			state.fileUploading = false;
			state.messageUploads = {};
		},
	},
});

export const {
	resetAfterSend,
	resetMessagesState,
	setChat,
	setNewMessage,
	setReadyToUploadFile,
	setSendToAPI,
	setUpdate,
	setFileUploading,
	updateMessageUploadStatus,
} = messages.actions;

export default messages.reducer;
